import type { ComponentChildren } from 'preact';

import { cx } from '@amumin/ui-core';

import styles from './EmptyState.module.css';

export interface EmptyStateProps {
  /** Wat er (nog) niet is, kort ("Vandaag niets gepland"). */
  title: string;
  /** Hooguit één zakelijke zin uitleg; staat gedempt achter de titel. */
  body?: ComponentChildren;
  /** Eén duidelijke vervolgactie (knop of link). */
  action?: ComponentChildren;
  /** web = pagina, compact = in een kaart of paneel (iets minder ruimte). */
  size?: 'web' | 'compact';
  name?: string;
  /** Titel als kop (h1 op een pagina zonder andere kop, zoals 404); standaard één tekstregel. */
  heading?: 'h1' | 'h2';
}

/** Een titel zonder slotteken krijgt een punt, zodat titel en uitleg één zin-regel vormen. */
function sentence(title: string): string {
  return /[.!?…。؟]$/.test(title.trim()) ? title : `${title}.`;
}

/**
 * Lege staat in de ouderapp (quick wins 6 okt 2026, docs/ux/quick-wins): één rustige tekstregel
 * in gewicht 400, zonder illustratie of gezichtje, met hooguit één actie eronder. De grote
 * EmptyState van ui-core (met illustratie) blijft voor het bord.
 */
export function EmptyState({ title, body, action, size = 'web', name, heading }: EmptyStateProps) {
  return (
    <div class={cx(styles.empty, size === 'compact' && styles.compact)} data-name={name}>
      {heading === 'h1' ? (
        <h1 class={styles.heading}>{title}</h1>
      ) : heading === 'h2' ? (
        <h2 class={styles.heading}>{title}</h2>
      ) : null}
      {heading ? (
        body ? (
          <p class={styles.line}>
            <span class={styles.body}>{body}</span>
          </p>
        ) : null
      ) : (
        <p class={styles.line}>
          <span class={styles.title}>{body ? sentence(title) : title}</span>
          {body ? (
            <>
              {' '}
              <span class={styles.body}>{body}</span>
            </>
          ) : null}
        </p>
      )}
      {action ? <div class={styles.action}>{action}</div> : null}
    </div>
  );
}
